import {
  UserTimeSegmentStat,
  FitnessGoalType,
  GoalType,
  EnvString,
  LevelType,
} from '@sworkit/api';
import { TypeKeys, ActionTypes } from '../actions/index';
import { defaultUserData } from '../global/firestore-user';
import { baseReleaseData } from '../global/set-new-release-to-badge';

const getInitialState = (): UserState => {
  return {
    ...defaultUserData,
    favorites: [],
    hasSignedIn: false,
    userJustSignedUp: false,
    recommendedLevel: null,
    recommendedGoal: null,
    userProfilePhotoUploading: false,
    userLoaded: false,
    userStats: {
      today: _getDefaultUserTimeSegmentStat(),
      week: _getDefaultUserTimeSegmentStat(),
      month: _getDefaultUserTimeSegmentStat(),
      lastMonth: _getDefaultUserTimeSegmentStat(),
      lastWeek: _getDefaultUserTimeSegmentStat(),
      year: _getDefaultUserTimeSegmentStat(),
      yearMonthStats: [],
    },
    userStatsLoaded: false,
    userStatsUpdating: false,
    loyalUser: false,
    loyalUserLoaded: false,
    legacyProUser: false,
    releaseData: baseReleaseData,
  };
};

const user = (state = getInitialState(), action: ActionTypes): UserState => {
  switch (action.type) {
    case TypeKeys.SET_USER_DOC: {
      const userDoc = action.userDoc;
      const activePlanSlug = userDoc?.plan?.currentPlanSlug;
      const [goal, level] = activePlanSlug?.split('-') as [GoalType, LevelType] || [];
      const recommendedLevel = level || userDoc?.profile?.activityLevel || 'beginner';
      const recommendedGoal =  goal || getRecommendedGoalFromFitnessGoal(
          userDoc?.profile?.goal
        );
      const releaseData = userDoc?.meta?.releaseData || state.releaseData;

      const allTimeStats =
        userDoc && userDoc.allTimeStats
          ? userDoc.allTimeStats
          : state.allTimeStats;
      const account =
        userDoc && userDoc.account ? userDoc.account : state.account;
      const env =
        userDoc && userDoc.account && userDoc.account.env
          ? getValidEnv(userDoc?.account?.env)
          : 'prod';
          const workoutSettings = userDoc.workoutSettings || {}
      return {
        ...state,
        ...userDoc,
        challenges: userDoc?.challenges,
        account: {
          ...account,
          env,
        },
        allTimeStats,
        recommendedGoal,
        recommendedLevel,
        userLoaded: true,
        releaseData,
        workoutSettings: {...workoutSettings}
      };
    }
    case TypeKeys.INCREASE_SESSION_COUNT: {
      return {
        ...state,
        allTimeStats: {
          ...state.allTimeStats,
          totalSessions: state.allTimeStats.totalSessions + 1,
        },
      };
    }
    case TypeKeys.SET_FAVORITES: {
      return {
        ...state,
        favorites: action.favorites,
      };
    }
    case TypeKeys.SET_USER_INTEGRATIONS: {
      return {
        ...state,
        integrations: {
          ...state.integrations,
          ...action.integrations,
        },
      };
    }
    case TypeKeys.GET_USER_STATS: {
      // leave this alone - the get call can take some time - but if we already have stats loaded - let's render them.
      return {
        ...state,
        userStatsUpdating: true,
        // userStatsLoaded: false
      };
    }

    case TypeKeys.SET_USER_STATS: {
      return {
        ...state,
        userStatsLoaded: true,
        userStatsUpdating: false,
        userStats: action.userStats,
      };
    }
    case TypeKeys.SET_USER_ACCOUNT: {
      const account = {
        ...state.account,
        ...action.account,
      };
      return {
        ...state,
        account,
      };
    }
    case TypeKeys.SET_USER_JUST_SIGNED_UP: {
      return {
        ...state,
        userJustSignedUp: true,
      };
    }
    case TypeKeys.SET_USER_LOCALE: {
      return {
        ...state,
        account: {
          ...state.account,
          locale: action.locale,
        },
      };
    }
    case TypeKeys.USER_PROFILE_PHOTO_UPLOADING: {
      return {
        ...state,
        profile: {
          ...state.profile,
          photo: action.photo,
        },
        userProfilePhotoUploading: true,
      };
    }
    case TypeKeys.USER_PROFILE_PHOTO_UPLOADED: {
      return {
        ...state,
        userProfilePhotoUploading: false,
      };
    }
    case TypeKeys.SIGN_IN: {
      return {
        ...state,
        hasSignedIn: true,
      };
    }
    case TypeKeys.SIGN_OUT: {
      return { ...getInitialState() };
    }
  }
  return state;
};

export default user;

function _getDefaultUserTimeSegmentStat() {
  const stats: UserTimeSegmentStat = {
    averageDuration: 0,
    totalSessions: 0,
    workoutTime: 0,
    caloriesBurned: 0,
    goldMedals: 0,
    silverMedals: 0,
    bronzeMedals: 0,
    activeDays: 0,
  };
  return stats;
}

function getRecommendedGoalFromFitnessGoal(
  fitnessGoal: FitnessGoalType
): GoalType {
  switch (fitnessGoal) {
    case 'GOAL_LOSE':
      return 'leaner';
    case 'GOAL_TONE':
      return 'leaner';
    case 'GOAL_BUILD':
      return 'stronger';
    case 'GOAL_SPORTS':
      return 'stronger';
    case 'GOAL_ENDURANCE':
      return 'leaner';
    case 'GOAL_INJURY':
      return 'custom';
    default:
      return 'fitter';
  }
}

function getValidEnv(env: EnvString) {
  const valid: { [key in EnvString]: EnvString } = {
    alpha: 'alpha',
    beta: 'beta',
    dev: 'dev',
    prod: 'prod',
    kids: 'kids'
  };
  if (valid[env]) {
    return valid[env];
  } else {
    return 'prod';
  }
}
